// StationPanelDetail – detail stanice: 6 sekcí (základní info, pozorování s tabs, prostředí, adresa, lokalizace, poznámka)
import { useElements, useStationTypes } from '../../hooks/useAppData'
import { Tabs, Chip, Separator } from '@heroui/react'
import { StationPhoto } from '../StationPhoto'

const BASE_URL = import.meta.env.VITE_API_URL || '/api'

function InfoRow({ label, value }) {
  if (!value) return null
  const lines = String(value).split(/\\n|\n/)
  return (
    <div className="flex gap-2 text-sm py-0.5">
      <span className="text-gray-400 w-40 shrink-0">{label}</span>
      <span className="font-medium">
        {lines.map((line, i) => (
          <span key={i}>{line}{i < lines.length - 1 && <br />}</span>
        ))}
      </span>
    </div>
  )
}

function SectionTitle({ children }) {
  return (
    <h3 className="font-semibold text-base mt-4 mb-2 text-gray-700 border-b border-gray-200 pb-1">
      {children}
    </h3>
  )
}

function ObservationsSection({ station, elementsMap }) {
  if (!station.observation || station.observation.length === 0) {
    return <p className="text-sm text-gray-400">Žádná aktivní pozorování.</p>
  }

  // Group by obs_type
  const groups = {}
  station.observation.forEach(obs => {
    if (!groups[obs.obs_type]) groups[obs.obs_type] = []
    groups[obs.obs_type].push(obs)
  })

  const OBS_TYPE_ORDER = ['DLY', '10M','HLY','PHENO']
  const obsTypeIds = Object.keys(groups).sort((a, b) => {
    const ai = OBS_TYPE_ORDER.indexOf(a)
    const bi = OBS_TYPE_ORDER.indexOf(b)
    if (ai === -1 && bi === -1) return a.localeCompare(b)
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })

  return (
    <Tabs defaultSelectedKey={obsTypeIds[0]}>
      <Tabs.ListContainer>
        <Tabs.List aria-label="Typy pozorování">
          {obsTypeIds.map((ot, i) => (
            <Tabs.Tab key={ot} id={ot}>
              {ot}
              {i > 0 && <Tabs.Separator />}
              <Tabs.Indicator />
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs.ListContainer>

      {obsTypeIds.map(ot => (
        <Tabs.Panel key={ot} id={ot} className="mt-3">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-xs text-gray-400 border-b">
                  <th className="pb-1 pr-3 font-medium">Zkratka</th>
                  <th className="pb-1 pr-3 font-medium">Název</th>
                  <th className="pb-1 pr-3 font-medium">Popis</th>
                  <th className="pb-1 pr-3 font-medium">Jednotka</th>
                  <th className="pb-1 pr-3 font-medium">Výška [m]</th>
                  <th className="pb-1 pr-3 font-medium">Plán</th>
                  <th className="pb-1 font-medium">Období</th>
                </tr>
              </thead>
              <tbody>
                {groups[ot].map((obs, idx) => {
                  const el = elementsMap[obs.el_abbreviation] || {}
                  const endLabel = obs.end_date
                    ? (obs.end_date.split('-')[0] === '3999' ? 'dosud' : (new Date(obs.end_date).toLocaleDateString('cs-CZ')))
                    : 'dosud'
                  return (
                    <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-1 pr-3 font-mono text-xs">{obs.el_abbreviation}</td>
                      <td className="py-1 pr-3">{el.name || '—'}</td>
                      <td className="py-1 pr-3 text-gray-500 max-w-[200px] truncate">{el.description || '—'}</td>
                      <td className="py-1 pr-3 text-gray-500">{el.unit || '—'}</td>
                      <td className="py-1 pr-3 text-gray-500">{obs.height ?? '—'}</td>
                      <td className="py-1 pr-3 text-gray-500">{obs.ms_scheme || '—'}</td>
                      <td className="py-1 text-xs text-gray-400 whitespace-nowrap">
                        {obs.begin_date
                          ? new Date(obs.begin_date).toLocaleDateString('cs-CZ')
                          : '?'}
                        {' → '}
                        {endLabel}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Tabs.Panel>
      ))}
    </Tabs>
  )
}

export function StationPanelDetail({ station }) {
  const { elementsMap } = useElements()
  const stationTypes = useStationTypes()

  if (!station) return null

  const openFullPhoto = async () => {
    try {
      const data = await fetch(`${BASE_URL}/img/geography?gh_id=${station.gh_id}&size=f`).then(r => r.json())
      if (!data.photo) return
      const win = window.open('', '_blank')
      win.document.write(`<html><body style="margin:0;background:#000;display:flex;align-items:center;justify-content:center;min-height:100vh"><img src="data:image/png;base64,${data.photo}" style="max-width:100%;max-height:100vh;object-fit:contain" /></body></html>`)
      win.document.close()
    } catch (e) {
      console.error('Failed to load full photo', e)
    }
  }

  return (
    <div className="px-4 pt-2 pb-4 max-w-4xl">

      {/* 1. Basic info */}
      <SectionTitle>1. Základní informace</SectionTitle>
      <div className="flex flex-col md:flex-row gap-6">
        <div className="flex-1">
          <InfoRow label="GH_ID" value={station.gh_id} />
          <InfoRow label="WSI" value={station.wsi} />
          <InfoRow label="Název" value={station.name} />
          <InfoRow label="Doplnění" value={station.qual} />
          <InfoRow label="Úplný název" value={station.full_name} />
          <hr className="my-2" />
          <InfoRow label="Období" value={station.begin_date ? `${new Date(station.begin_date).toLocaleDateString('cs-CZ')} – ${station.end_date.split('-')[0] === '3999' ? 'dosud' : new Date(station.end_date).toLocaleDateString('cs-CZ')}` : null} />
          <hr className="my-2" />
          <InfoRow label="Kraj" value={station.h2} />
          <InfoRow label="Okres" value={station.h3} />
          <InfoRow label="ORP" value={station.h4} />
          <hr className="my-2" />
          <InfoRow label="Povodí" value={station.basin} />
          <hr className="my-2" />
          <InfoRow label="Vlastník" value={station.sow_name} />
          <hr className="my-2" />
          <InfoRow label="WMO ID" value={station.wmo_id} />
          <InfoRow label="ICAO" value={station.icao_id} />
          {(() => {
            const typeObj = stationTypes.find(t => t.id === station.station_type)
            const typeLabel = station.station_type
              ? typeObj
                ? `${station.station_type} – ${typeObj.item}`
                : station.station_type
              : null
            return <InfoRow label="Typ stanice" value={typeLabel} />
          })()}
        </div>
        <div className="shrink-0">
          <button onClick={openFullPhoto} title="Otevřít plnou velikost" className="block">
            <StationPhoto
              ghId={station.gh_id}
              size="m"
              className="w-[300px] h-[250px] rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
              alt={station.name}
            />
          </button>
          <p className="text-xs text-gray-400 text-center mt-1">Klikni pro plnou velikost</p>
        </div>
      </div>

      <Separator className="my-4" />

      {/* 2. Observations */}
      <SectionTitle>2. Pozorování</SectionTitle>
      <ObservationsSection station={station} elementsMap={elementsMap} />

      <Separator className="my-4" />

      {/* 3. Environment */}
      <SectionTitle>3. Prostředí stanice</SectionTitle>
      <InfoRow label="Pedologie typ" value={station.pedolog_type} />
      <InfoRow label="Poznámka k pedologii" value={station.pedolog_note} />
      <hr className="my-2" />
      <InfoRow label="Vegetace" value={station.plant_cover} />
      <InfoRow label="Poznámka k vegetaci" value={station.plant_cover_note} />
      <hr className="my-2" />
      <InfoRow label="Typ reliéfu" value={station.relief_type} />
      <InfoRow label="Poznámka k reliéfu" value={station.relief_note} />
      <hr className="my-2" />
      <InfoRow label="Antropogenního vliv" value={station.antropo_type} />
      <InfoRow label="Poznámka k antr. vlivu" value={station.antropo_note} />

      <Separator className="my-4" />

      {/* 4. Address */}
      <SectionTitle>4. Adresa</SectionTitle>
      <InfoRow label="Název" value={station.address_title} />
      <InfoRow label="Ulice" value={station.address_street} />
      <InfoRow label="Město" value={station.address_city} />
      <InfoRow label="PSČ" value={station.address_code} />
      {/*<InfoRow label="Poznámka" value={station.address_note} />
      <InfoRow label="E-mail" value={station.address_email} />
      <InfoRow label="Telefon" value={station.address_phone} />
      <InfoRow label="Kontaktní osoba" value={station.address_contact_person} />
      */}

      <Separator className="my-4" />

      {/* 5. Location */}
      <SectionTitle>5. Lokalizace</SectionTitle>
      <InfoRow label="Nadmořská výška" value={station.elevation != null ? `${station.elevation} m` : null} />
      <InfoRow label="Zeměpisná šířka" value={station.latitude != null ? `${station.latitude}°` : null} />
      <InfoRow label="Zeměpisná délka" value={station.longitude != null ? `${station.longitude}°` : null} />
      <InfoRow label="X (UTM – gx)" value={station.gx} />
      <InfoRow label="Y (UTM – gy)" value={station.gy} />
      <InfoRow label="X (S-JTSK – jx)" value={station.jx} />
      <InfoRow label="Y (S-JTSK – jy)" value={station.jy} />

      {station.note && (
        <>
          <Separator className="my-4" />
          <SectionTitle>6. Poznámka</SectionTitle>
          <p className="text-sm text-gray-700">
            {station.note.split('\\n').map((line, i, arr) => (
              <span key={i}>
                {line}
                {i < arr.length - 1 && <br />}
              </span>
            ))}
          </p>
        </>
      )}
    </div>
  )
}
